/**
 * TestDefinitionDetailPane.tsx
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — B2/B3: the right pane.
 * ONE form for the whole test definition (catalog item + primary task type),
 * in sections: Identity / Pricing / Scheduling / Text / Links / Advanced
 * recipe, with a sticky footer (Delete · Discard · Save).
 *
 * Owns the DRAFT (seeded from the selected item, or blank in create mode) and
 * client-side validation; payload assembly is pure (testDefinitionModel).
 * All API calls stay in the page shell — the pane hands payloads up.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, FormControlLabel, Stack, Switch,
  Typography, alpha, useTheme,
} from '@mui/material';
import { DeleteOutline, ListAlt } from '@mui/icons-material';
import type { Skill, TaskType } from '../../../types/schedule';
import type {
  CreateDefinitionRequest,
  TestDefinitionItem,
  UpdateDefinitionRequest,
} from '../../../services/testDefinitionService';
import type { MachineOption } from './testCatalogHelpers';
import {
  buildCreatePayload,
  buildUpdatePayload,
  deriveDefinitionFlags,
  draftFromItem,
  isDraftDirty,
  validateDraft,
  type DefinitionDraft,
} from './testDefinitionModel';
import { IdentitySection, PricingSection, TextSection } from './TestDefinitionFormSections';
import { TestDefinitionSchedulingSection } from './TestDefinitionSchedulingSection';
import { TestDefinitionLinksSection } from './TestDefinitionLinksSection';
import { TestDefinitionRecipeSection } from './TestDefinitionRecipeSection';
import { DefinitionStatusChips } from './TestDefinitionListColumn';

export interface MachinesEditorTarget {
  key: string;
  name: string;
  runMode: 'attended' | 'continuous';
}

interface TestDefinitionDetailPaneProps {
  /** Selected item (edit mode), or null (create mode / nothing selected). */
  item: TestDefinitionItem | null;
  createMode: boolean;
  totalActive: number;
  skills: Skill[];
  machineOptions: MachineOption[];
  capableMap: Record<string, string[]>;
  allTaskTypes: TaskType[];
  saving: boolean;
  repairing: boolean;
  /** Bumped by the page after a successful save → reseeds the draft. */
  resetToken: number;
  onSave: (payload: UpdateDefinitionRequest) => void;
  onCreate: (payload: CreateDefinitionRequest) => void;
  onDelete: (item: TestDefinitionItem) => void;
  onDirtyChange: (dirty: boolean) => void;
  onOpenMachinesEditor: (target: MachinesEditorTarget) => void;
  onCreateMatchingTaskType: (item: TestDefinitionItem) => void;
  onOpenPrerequisites: (item: TestDefinitionItem) => void;
  onOpenMethodology: (item: TestDefinitionItem) => void;
  onRecipeChanged: () => void;
  onCancelCreate: () => void;
}

export const TestDefinitionDetailPane = ({
  item,
  createMode,
  totalActive,
  skills,
  machineOptions,
  capableMap,
  allTaskTypes,
  saving,
  repairing,
  resetToken,
  onSave,
  onCreate,
  onDelete,
  onDirtyChange,
  onOpenMachinesEditor,
  onCreateMatchingTaskType,
  onOpenPrerequisites,
  onOpenMethodology,
  onRecipeChanged,
  onCancelCreate,
}: TestDefinitionDetailPaneProps) => {
  const theme = useTheme();
  const [draft, setDraft] = useState<DefinitionDraft>(() => draftFromItem(item));
  const [validationError, setValidationError] = useState<string | null>(null);
  // B3: "Also create matching task type" switch — default ON (D2).
  const [createTaskType, setCreateTaskType] = useState(true);

  const itemKey = item?.TestCatalogItemKey ?? null;

  // Reseed on selection change, mode change, or after a successful save.
  useEffect(() => {
    setDraft(draftFromItem(createMode ? null : item));
    setValidationError(null);
    if (createMode) setCreateTaskType(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey, createMode, resetToken]);

  const setField = useCallback(<K extends keyof DefinitionDraft>(
    field: K,
    value: DefinitionDraft[K],
  ) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const dirty = useMemo(
    () => isDraftDirty(draft, createMode ? null : item),
    [draft, item, createMode],
  );
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  // ── Empty state ─────────────────────────────────────────────────────────────

  if (!createMode && !item) {
    return (
      <Box
        sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '100%', p: 4, textAlign: 'center',
        }}
      >
        <ListAlt sx={{ fontSize: 44, color: theme.palette.text.disabled, mb: 1 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Pick a test on the left
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mt: 0.5 }}>
          Everything about a test lives here — its details, pricing, machine time, required
          skill, machines, lab mapping, prerequisites, and methodology — saved together with
          one click. {totalActive} active test{totalActive === 1 ? '' : 's'} in the catalogue.
        </Typography>
      </Box>
    );
  }

  // ── Save ────────────────────────────────────────────────────────────────────

  const editsTaskType = createMode
    ? createTaskType
    : item?.primaryTaskType != null;
  const capabilityCount = createMode ? 0 : item?.primaryTaskType?.CapabilityCount ?? 0;

  const handleSave = () => {
    const error = validateDraft(draft, { editsTaskType, capabilityCount });
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    if (createMode) {
      onCreate(buildCreatePayload(draft, createTaskType));
    } else if (item) {
      onSave(buildUpdatePayload(draft, item));
    }
  };

  const handleDiscard = () => {
    if (createMode) {
      onCancelCreate();
      return;
    }
    setDraft(draftFromItem(item));
    setValidationError(null);
  };

  const flags = item && !createMode ? deriveDefinitionFlags(item) : null;
  const trimmedName = draft.TestName.trim();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {createMode ? 'New Test' : trimmedName || 'Test'}
          </Typography>
          {!createMode && draft.Category && (
            <Chip size="small" variant="outlined" label={draft.Category} sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
          {flags && <DefinitionStatusChips flags={flags} />}
        </Box>
        {createMode && (
          <Box sx={{ mt: 0.5 }}>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={createTaskType}
                  onChange={(e) => setCreateTaskType(e.target.checked)}
                />
              )}
              label={(
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  Also create matching task type
                </Typography>
              )}
            />
            {!createTaskType && (
              <Alert severity="warning" sx={{ mt: 0.5, py: 0.5 }}>
                Without a task type this test is quotable but NOT schedulable — work-order
                lines on it generate no schedule tasks until a recipe step is added.
              </Alert>
            )}
          </Box>
        )}
      </Box>

      <Divider />

      {/* Scrollable body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <IdentitySection draft={draft} setField={setField} showTaskTypeFields={editsTaskType} />
        <PricingSection draft={draft} setField={setField} />
        <TestDefinitionSchedulingSection
          draft={draft}
          setField={setField}
          item={createMode ? null : item}
          showTaskTypeFields={editsTaskType}
          skills={skills}
          machineOptions={machineOptions}
          capableMap={capableMap}
          onOpenMachinesEditor={() => {
            if (item?.primaryTaskType) {
              onOpenMachinesEditor({
                key: item.primaryTaskType.TaskTypeKey,
                name: item.primaryTaskType.Name,
                runMode: draft.ttRunMode,
              });
            }
          }}
          onCreateMatchingTaskType={() => { if (item) onCreateMatchingTaskType(item); }}
          repairing={repairing}
        />
        <TextSection draft={draft} setField={setField} />
        {!createMode && item && (
          <>
            <TestDefinitionLinksSection
              item={item}
              onOpenPrerequisites={() => onOpenPrerequisites(item)}
              onOpenMethodology={() => onOpenMethodology(item)}
            />
            <TestDefinitionRecipeSection
              catalogKey={item.TestCatalogItemKey}
              recipeSize={item.recipeSize}
              primaryTaskTypeKey={item.primaryTaskType?.TaskTypeKey ?? null}
              allTaskTypes={allTaskTypes}
              skills={skills}
              onChanged={onRecipeChanged}
            />
          </>
        )}
      </Box>

      {/* Sticky footer */}
      <Box
        sx={{
          p: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {validationError && (
          <Alert severity="error" sx={{ py: 0, flex: 1 }} onClose={() => setValidationError(null)}>
            {validationError}
          </Alert>
        )}
        {!validationError && !createMode && item && (
          <Button
            color="error"
            size="small"
            startIcon={<DeleteOutline />}
            onClick={() => onDelete(item)}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={handleDiscard}
            disabled={saving || (!createMode && !dirty)}
            sx={{ textTransform: 'none' }}
          >
            {createMode ? 'Cancel' : 'Discard'}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving || !dirty || trimmedName.length === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Saving…' : createMode ? 'Create Test' : 'Save'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
